// Formatting and color rules for the Forecasting tab's Phone and Chat view
// (GET /api/admin/reporting/forecasting/phone-chat). Kept apart from the
// component so the thresholds Patrick approved are unit tested
// (ui/test/forecastPhoneChat.test.js). A tone is 'error', 'warning' or null; the
// component turns it into a theme color. Every tone is judged on the number AS
// SHOWN (rounded to the shown precision), so a cell never reads "+1.0" in amber
// or "23.0%" in red.

const r1 = (n) => Math.round(n * 10) / 10
const finite = (n) => typeof n === 'number' && Number.isFinite(n)

export const EM_DASH = '—'

// Seconds as m:ss ("2:00"); a missing value is a dash, never 0:00.
export function formatSeconds(sec) {
  if (!finite(sec)) return EM_DASH
  const total = Math.round(sec)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

// A fraction as a percent with one decimal ("5.0%").
export function formatRate(fraction) {
  return finite(fraction) ? `${(fraction * 100).toFixed(1)}%` : EM_DASH
}

export function formatCount(n) {
  return finite(n) ? Math.round(n).toLocaleString('en-US') : EM_DASH
}

// FTE with one decimal; "-0.0" never appears.
export function formatFte(n) {
  if (!finite(n)) return EM_DASH
  const v = r1(n)
  return (v === 0 ? 0 : v).toFixed(1)
}

export function formatGap(n) {
  if (!finite(n)) return EM_DASH
  return `${r1(n) > 0 ? '+' : ''}${formatFte(n)}`
}

export function formatHoursShort(h) {
  return finite(h) ? `${Math.round(h).toLocaleString('en-US')} h` : null
}

// A fraction (0.063) as a signed percent ("+6.3%").
export function formatVariance(fraction) {
  if (!finite(fraction)) return EM_DASH
  const pct = r1(fraction * 100)
  return `${pct > 0 ? '+' : ''}${(pct === 0 ? 0 : pct).toFixed(1)}%`
}

// A fraction as a plain percent with one decimal ("29.8%").
export function formatPercent(fraction) {
  return finite(fraction) ? `${r1(fraction * 100).toFixed(1)}%` : EM_DASH
}

// Variance: amber at 10% either way, red at 20%.
export function varianceTone(fraction) {
  if (!finite(fraction)) return null
  const pct = Math.abs(r1(fraction * 100))
  return pct >= 20 ? 'error' : pct >= 10 ? 'warning' : null
}

// Staffing gap in FTE: red below zero, amber under +1.
export function gapTone(gap) {
  if (!finite(gap)) return null
  const v = r1(gap)
  return v < 0 ? 'error' : v < 1 ? 'warning' : null
}

// Speed of answer: red above the 2:00 goal (a whole second, as shown).
export function asaTone(sec, goalSec = 120) {
  if (!finite(sec)) return null
  return Math.round(sec) > goalSec ? 'error' : null
}

// Abandon rate: amber above 5%, red above 7%.
export function abaTone(fraction) {
  if (!finite(fraction)) return null
  const pct = r1(fraction * 100)
  return pct > 7 ? 'error' : pct > 5 ? 'warning' : null
}

// Realized shrinkage against the 23% standard: amber above 23%, red above 30%.
export function shrinkageTone(fraction) {
  if (!finite(fraction)) return null
  const pct = r1(fraction * 100)
  return pct > 30 ? 'error' : pct > 23 ? 'warning' : null
}

// The day table: hours short of the requirement and agents short per open hour.
export function shortHoursTone(hours) {
  if (!finite(hours)) return null
  const v = r1(hours)
  return v >= 10 ? 'error' : v >= 5 ? 'warning' : null
}
export function shortPerHourTone(perHour) {
  if (!finite(perHour)) return null
  const v = Math.round(perHour * 100) / 100
  return v >= 1 ? 'error' : v >= 0.4 ? 'warning' : null
}

// "2026-10-17" -> "10/17".
export function weekLabel(week) {
  return typeof week === 'string' && week.length >= 10 ? `${week.slice(5, 7)}/${week.slice(8, 10)}` : week
}

// The small tag beside a week: this week, or an event week excluded from the
// forecast baseline.
export function weekTags(row) {
  const tags = []
  if (row.isThisWeek) tags.push('this week')
  if (row.isEventWeek) tags.push('event week')
  return tags
}
